import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/* Shown once per Terms version to client users until they accept. Admins are exempt. */
export default function TermsGate() {
    const { user, refresh } = useAuth();
    const [checked, setChecked] = useState(false);
    const [busy, setBusy] = useState(false);

    const needs =
        user && user.role === "client" && user.terms_current_version && user.terms_accepted_version !== user.terms_current_version;
    if (!needs) return null;

    async function accept() {
        setBusy(true);
        try {
            await api.post("/account/accept-terms");
            await refresh();
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="terms-gate-title"
            data-testid="terms-gate"
        >
            <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-[#0f0f10] p-6 sm:p-7 shadow-2xl">
                <p className="text-[11.5px] uppercase tracking-[0.18em] text-zinc-500">Before you continue</p>
                <h2 id="terms-gate-title" className="mt-2 text-xl font-semibold text-zinc-50">
                    Please review our terms
                </h2>
                <p className="mt-3 text-[13.5px] text-zinc-400 leading-relaxed">
                    We've published our Terms of Service, Privacy Policy and Data Processing Addendum (version{" "}
                    {user.terms_current_version}). They explain how we protect your business and your customers' information.
                </p>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
                    <Link to="/terms" target="_blank" className="text-zinc-200 underline underline-offset-4" data-testid="terms-gate-terms-link">Terms</Link>
                    <Link to="/privacy" target="_blank" className="text-zinc-200 underline underline-offset-4" data-testid="terms-gate-privacy-link">Privacy</Link>
                    <Link to="/dpa" target="_blank" className="text-zinc-200 underline underline-offset-4" data-testid="terms-gate-dpa-link">DPA</Link>
                </div>
                <label className="mt-6 flex items-start gap-3 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                        data-testid="terms-gate-checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-zinc-900 accent-zinc-100"
                    />
                    <span className="text-[13.5px] text-zinc-300">
                        I have read and agree to the Terms of Service, Privacy Policy and DPA on behalf of my business.
                    </span>
                </label>
                <button
                    type="button"
                    disabled={!checked || busy}
                    onClick={accept}
                    data-testid="terms-gate-accept-button"
                    className="mt-6 w-full h-11 rounded-md bg-zinc-50 text-zinc-950 text-[14px] font-medium hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    Agree and continue
                </button>
            </div>
        </div>
    );
}
